import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, makeStyles, mergeClasses, tokens } from '@coreai/fluentui-react';
import { CopyRegular, CheckmarkRegular } from '@fluentui/react-icons';
import { OttoLogo } from '@/components/OttoLogo';
import { useAppStore } from '@/stores/appStore';
import { getBuildInfo, formatBuildInfoForClipboard } from '@/utils/system';

const environmentAppearance: Record<string, 'informative' | 'warning' | 'success' | 'important'> = {
  development: 'informative',
  canary: 'warning',
  production: 'success',
};

// Browser+version, detected once at module load.
// Order matters because UA strings overlap: Edge contains "Chrome", Chrome
// contains "Safari", Opera contains "Chrome". Match the most specific first.
// Detection is intentionally best-effort — this surface is diagnostic, not
// load-bearing, so we trade exhaustive coverage for a tiny dependency-free
// implementation. Falls back to "Unknown" when nothing matches (rare).
function detectBrowserLabel(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent;
  const patterns: Array<readonly [string, RegExp]> = [
    ['Edge', /Edg\/(\d+(?:\.\d+)*)/],
    ['Opera', /OPR\/(\d+(?:\.\d+)*)/],
    ['Firefox', /Firefox\/(\d+(?:\.\d+)*)/],
    ['Chrome', /Chrome\/(\d+(?:\.\d+)*)/],
    ['Safari', /Version\/(\d+(?:\.\d+)*)\s+.*Safari/],
  ];
  for (const [name, re] of patterns) {
    const match = ua.match(re);
    if (match) return `${name} ${match[1]}`;
  }
  return 'Unknown';
}

const BROWSER_LABEL = detectBrowserLabel();

const useAboutStyles = makeStyles({
  // Flat hero row: Auto logo + name/version + Copy diagnostics button.
  // Sits directly on the modal background, matching the borderless list
  // pattern used by the other sections. A hairline under the hero
  // separates it from the details grid below.
  hero: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    paddingBlockEnd: tokens.spacingVerticalL,
    borderBlockEndWidth: '1px',
    borderBlockEndStyle: 'solid',
    borderBlockEndColor: tokens.colorNeutralStroke3,
    marginBlockEnd: tokens.spacingVerticalM,
  },
  heroLogo: {
    flexShrink: 0,
    display: 'inline-flex',
  },
  heroText: {
    flex: 1,
    minWidth: 0,
  },
  heroName: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  heroSubline: {
    margin: 0,
    marginBlockStart: tokens.spacingVerticalXXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
  },

  // Details: flat list of label/value rows, no surrounding card. Rows
  // separated by hairlines — same visual rhythm as the other sections.
  details: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(160px, 200px) 1fr',
    alignItems: 'center',
    paddingBlock: tokens.spacingVerticalM,
    ':not(:first-child)': {
      borderBlockStartWidth: '1px',
      borderBlockStartStyle: 'solid',
      borderBlockStartColor: tokens.colorNeutralStroke3,
    },
  },
  rowLabel: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  rowValue: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    fontVariantNumeric: 'tabular-nums',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowMono: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },
});

/**
 * Replaces the standalone AboutDialog with an in-modal section.
 *
 * Flat layout (no surrounding cards):
 *   - Hero row: Auto logo + product name + version/build subline + Copy
 *   - Details: row-separated list of portal / build / environment / runtime / region
 */
export const AboutSection = memo(() => {
  const styles = useAboutStyles();
  const info = getBuildInfo();
  const selectedLocation = useAppStore((s) => s.selectedLocation);
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleCopy = useCallback(async () => {
    try {
      // Append the runtime + region the user sees in the UI so the copied
      // payload matches the rendered rows. `formatBuildInfoForClipboard`
      // covers the static fields; we tack on the dynamic ones here.
      const text = [
        formatBuildInfoForClipboard(info),
        `Runtime: ${BROWSER_LABEL}`,
        `Region: ${selectedLocation ?? 'All regions'}`,
      ].join('\n');
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available in all contexts; the user can
      // still read the values from the rendered rows below.
    }
  }, [info, selectedLocation]);

  const versionLabel = `v${info.version}`;
  const subline = info.buildId && info.buildId !== 'local'
    ? `${versionLabel} · build ${info.buildId}`
    : versionLabel;

  return (
    <>
      <div className={styles.hero}>
        <div className={styles.heroLogo} aria-hidden="true">
          <OttoLogo size={44} />
        </div>
        <div className={styles.heroText}>
          <p className={styles.heroName}>Auto Portal</p>
          <p className={styles.heroSubline}>{subline}</p>
        </div>
        <Button
          appearance="secondary"
          size="small"
          icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
          onClick={handleCopy}
        >
          {copied ? 'Copied' : 'Copy diagnostics'}
        </Button>
      </div>

      <div className={styles.details}>
        <div className={styles.row}>
          <span className={styles.rowLabel}>Portal</span>
          <span className={styles.rowValue}>{versionLabel}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowLabel}>Build</span>
          <span className={mergeClasses(styles.rowValue, styles.rowMono)}>{info.buildId}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowLabel}>Environment</span>
          <span className={styles.rowValue}>
            <Badge appearance="filled" color={environmentAppearance[info.environment] ?? 'informative'}>
              {info.environment}
            </Badge>
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowLabel}>Runtime</span>
          <span className={styles.rowValue}>{BROWSER_LABEL}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowLabel}>Region</span>
          <span className={styles.rowValue}>{selectedLocation ?? 'All regions'}</span>
        </div>
      </div>
    </>
  );
});
AboutSection.displayName = 'AboutSection';
